import type {
  DetailProductDto,
  ReviewListDto,
  ProductListDto,
  CrossSellingProductDto,
  FilterAttributeDto,
  LocationSuggestionsDto,
  ProductApiFindAllRequest,
  DetailGiftCardProductDto,
} from '@checkfront/guest-experience-api-api-client-javascript-axios';
import axios, { AxiosResponse } from 'axios';

import { LanguageLocaleCode } from '../i18n/i18n';
import { PlatformEnum } from '../types/platform';

import { createHeaders } from './createHeaders';
import { createParams } from './createParams';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_GUEST_EXPERIENCE_URL + '/api/v1',
});

export type GetProductsParams = Omit<
  ProductApiFindAllRequest,
  'xTenant' | 'xApplicationId' | 'xPartnerCode' | 'xLocale'
>;

export const productApi = {
  getProducts(customerCode: string, locale: LanguageLocaleCode, params?: GetProductsParams) {
    const headers = createHeaders(customerCode);
    return unwrap(
      axiosInstance.get<ProductListDto>('products', {
        headers,
        params: {
          ...params,
          ...createParams(locale),
        },
      })
    );
  },

  getProduct(id: string, customerCode: string, locale: LanguageLocaleCode) {
    const headers = createHeaders(customerCode);
    return unwrap(
      axiosInstance.get<DetailProductDto>(`products/${id}`, {
        headers,
        params: createParams(locale),
      })
    );
  },

  getReviews(
    id: string,
    customerCode: string,
    locale: LanguageLocaleCode,
    params?: { limit?: number; offset?: number }
  ) {
    const headers = createHeaders(customerCode);
    return unwrap(
      axiosInstance.get<ReviewListDto>(`products/${id}/reviews`, {
        headers,
        params: {
          ...params,
          ...createParams(locale),
        },
      })
    );
  },

  getCrossSelling(id: string, customerCode: string, locale: LanguageLocaleCode) {
    const headers = createHeaders(customerCode);
    return unwrap(
      axiosInstance.get<CrossSellingProductDto[]>(`products/${id}/cross-selling`, {
        headers,
        params: createParams(locale),
      })
    );
  },

  getFilterAttributes(
    customerCode: string,
    locale: LanguageLocaleCode,
    params?: GetProductsParams
  ) {
    const headers = createHeaders(customerCode);
    return unwrap(
      axiosInstance.get<FilterAttributeDto[]>(`products/filter-attributes`, {
        headers,
        params: {
          ...params,
          ...createParams(locale),
        },
      })
    );
  },
};

export const locationApi = {
  getSuggestedLocation(customerCode: string, locale: LanguageLocaleCode, keyword: string) {
    const headers = createHeaders(customerCode);
    return unwrap(
      axiosInstance.get<LocationSuggestionsDto>(`locations/suggest`, {
        headers,
        params: {
          keyword,
          ...createParams(locale),
        },
      })
    );
  },
};

export const regionApi = {
  getRegions(countryCode: string, locale?: LanguageLocaleCode) {
    return unwrap(
      axiosInstance.get<string[]>(`locations/regions`, {
        params: { countryCode, ...(locale ? createParams(locale) : {}) },
      })
    );
  },
};

export const unwrap = async <T>(responsePromise: Promise<AxiosResponse<T>>): Promise<T> =>
  (await responsePromise).data;

export const giftCardProductApi = {
  getGiftCardDetails(id: string, customerCode: string, locale: LanguageLocaleCode) {
    const headers = createHeaders(customerCode);
    return unwrap(
      axiosInstance.get<DetailGiftCardProductDto>(`giftcard-products/${id}`, {
        headers,
        params: createParams(locale),
      })
    );
  },
};

export const companyApi = {
  getCustomerPlatform(customerCode: string, locale: LanguageLocaleCode) {
    const headers = createHeaders(customerCode);
    return unwrap(
      axiosInstance.get<{ platform: PlatformEnum; url?: string }>('customer/platform/', {
        headers,
        params: createParams(locale),
      })
    );
  },
};
